import type { MarkdownDocument } from '@markbird/shared';

/** Normalize a single tag: trim, collapse inner whitespace, cap length. */
export function normalizeTag(raw: string): string | null {
  const t = raw.trim().replace(/\s+/g, ' ').slice(0, 32);
  return t.length > 0 ? t : null;
}

/**
 * Parse a free-text tag input (comma-separated) into a clean, de-duplicated
 * list. Duplicates are removed case-insensitively, keeping the first casing.
 */
export function parseTags(input: string): string[] {
  return dedupeTags(
    input
      .split(',')
      .map(normalizeTag)
      .filter((t): t is string => t !== null),
  );
}

/** De-duplicate tags case-insensitively, preserving first-seen order/casing. */
export function dedupeTags(tags: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of tags) {
    const t = normalizeTag(raw);
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

/** True if the document carries `tag` (case-insensitive). */
export function docHasTag(doc: Pick<MarkdownDocument, 'tags'>, tag: string): boolean {
  const key = tag.toLowerCase();
  return (doc.tags ?? []).some((t) => t.toLowerCase() === key);
}

/** All distinct tags across documents, sorted alphabetically (case-insensitive). */
export function allTags(docs: Pick<MarkdownDocument, 'tags'>[]): string[] {
  const map = new Map<string, string>(); // lowercase -> display
  for (const doc of docs) {
    for (const raw of doc.tags ?? []) {
      const t = normalizeTag(raw);
      if (!t) continue;
      const key = t.toLowerCase();
      if (!map.has(key)) map.set(key, t);
    }
  }
  return [...map.values()].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
}
